import React, { useMemo, useCallback, useRef } from 'react'

// eslint-disable-next-line max-params
function calculateShownRange(
  splitterIndex,
  colCount,
  mergeCount,
  colRights,
  stickyWidth,
  scrollLeft,
  colLefts,
  clientWidth
) {
  const result = []
  for (let i = splitterIndex + 1; i < colCount; i++) {
    // Merging from
    // For mergeCount <= 1: from = i
    const from = splitterIndex + 1 + mergeCount * Math.floor((i - splitterIndex - 1) / mergeCount)
    // Merging to
    // For mergeCount <= 1: to = i
    const to = from + mergeCount - 1 < colCount ? from + mergeCount - 1 : colCount - 1
    // Collect showing group cells index bounds
    if (colRights[i] > stickyWidth + scrollLeft && colLefts[i] < clientWidth + scrollLeft) {
      result.push([from, to, i])
    }
  }
  // Return bounding points
  return result.length
    ? [result[0][0], result[result.length - 1][1], result[0][2], result[result.length - 1][2]]
    : [splitterIndex, splitterIndex, splitterIndex, splitterIndex]
}

function calculateColumnWidths(rowCount, colCount, getColWidth) {
  const result = []
  if (rowCount > 0) {
    for (let i = 0; i < colCount; i++) {
      result.push(getColWidth(i))
    }
  }
  return result
}

function calculateColumnStartingPositions(rowCount, colCount, colWidths) {
  const result = []
  if (rowCount > 0) {
    for (let i = 0; i < colCount; i++) {
      if (0 === i) {
        result.push(0)
      } else {
        result.push(result[i - 1] + colWidths[i - 1])
      }
    }
  }
  return result
}

function calculateColumnEndPositions(rowCount, colCount, colWidths) {
  const result = []
  if (rowCount > 0) {
    for (let i = 0; i < colCount; i++) {
      if (0 === i) {
        result.push(colWidths[i])
      } else {
        result.push(result[i - 1] + colWidths[i])
      }
    }
  }
  return result
}

const useCells = props => {
  // Array of cell component array
  const rows = useMemo(() => props.rows || [], [props.rows])
  // Row count
  const rowCount = rows.length
  // Just first row
  const firstRow = useMemo(() => (rows.length ? props.rows[0] : []), [props.rows, rows.length])
  // Count of fixed columns without splitter
  const fixedColumns = props.fixedColumns || 0
  // Splitter index
  const splitterIndex = fixedColumns <= firstRow.length ? fixedColumns : 0
  // Count of fixed columns including splitter
  // const stickyColumns = splitterIndex > 0 ? fixedColumns + 1 : fixedColumns
  // Columns count including splitter
  const colCount = splitterIndex > 0 ? firstRow.length + 1 : firstRow.length
  // Container width
  const clientWidth = props.clientWidth || 0
  // Scroll left position
  const scrollLeft = props.scrollLeft || 0
  // Row index for span draw
  const spanRowIndex = props.spanRowIndex >= 0 ? props.spanRowIndex : -1
  // Start span column inside span row
  const spanColIndex = props.spanColIndex || 0
  // Start extra column index
  const extraColumnIndex = props.extraColumns ? colCount - props.extraColumns : 0

  // Just class name
  const className = props.className

  // Span row render function
  const renderSpan = useRef()
  renderSpan.current = props.renderSpan || (() => null)

  const mergeCount = useMemo(() => (props.group?.count > 1 ? props.group.count : 1), [props.group])
  const mergeRows = useMemo(() => props.group?.merge || [], [props.group])
  const collapse = useMemo(() => props.group?.collapse || [], [props.group])

  // Calculates default column width depending on column index
  const getColWidth = useCallback(
    colIndex => {
      if (0 === colIndex) return props.captionWidth
      else if (splitterIndex === colIndex) return props.splitterWidth + props.gapWidth
      else if (extraColumnIndex > 0 && extraColumnIndex <= colIndex) return props.extraColumnWidth + props.gapWidth
      else return props.columnWidth + props.gapWidth
    },
    [
      extraColumnIndex,
      props.captionWidth,
      props.columnWidth,
      props.extraColumnWidth,
      props.gapWidth,
      props.splitterWidth,
      splitterIndex
    ]
  )

  // Array of default column widths
  const colWidths = useMemo(
    () => calculateColumnWidths(rowCount, colCount, getColWidth),
    [colCount, getColWidth, rowCount]
  )

  const templateColumns = useMemo(() => colWidths.map(width => `${width}px`).join(' '), [colWidths])

  // Array of default column start positions
  const colLefts = useMemo(
    () => calculateColumnStartingPositions(rowCount, colCount, colWidths),
    [colCount, colWidths, rowCount]
  )

  // Array of default column right edges
  const colRights = useMemo(
    () => calculateColumnEndPositions(rowCount, colCount, colWidths),
    [colCount, colWidths, rowCount]
  )

  // Width of fixed left area including splitter
  const stickyWidth = useMemo(() => colRights[splitterIndex], [colRights, splitterIndex])

  // Calculate showing columns interval (indexes)
  const [showFrom, showTo, colFrom, colTo] = useMemo(
    () =>
      calculateShownRange(
        splitterIndex,
        colCount,
        mergeCount,
        colRights,
        stickyWidth,
        scrollLeft,
        colLefts,
        clientWidth
      ),
    [clientWidth, colCount, colLefts, colRights, scrollLeft, splitterIndex, stickyWidth, mergeCount]
  )

  /**
   * Calculates cell property including:
   *
   * name:
   *   cell - common showing cell
   *   span - spanned whole row
   *   left - spanned hidden left columns
   *   right - spanned hidden right columns
   *   splitter - splitter cell
   * left: - cell left edge position
   * width: - cell width
   * from: - span column start index
   * to: span column end index
   *
   * To skip sell return an empty object {}
   */
  const getCellType = useCallback(
    (rowIndex, colIndex) => {
      if (spanRowIndex === rowIndex) {
        // Draw span row
        if (colIndex < spanColIndex) {
          // Do not span fixed columns
          // Fixed cell mast have left property
          return {
            name: 'cell',
            left: colLefts[colIndex],
            width: colWidths[colIndex]
          }
        } else if (colIndex === spanColIndex) {
          // Draw just start span column
          // Setting left property makes it fixed
          let width = clientWidth
          for (let i = 0; i < spanColIndex; i++) width -= colWidths[i]
          return {
            name: 'span',
            left: colLefts[spanColIndex],
            width,
            from: spanColIndex,
            to: colCount - 1
          }
        } else {
          // Skip other spanned columns
          return {}
        }
      } else {
        // Draw common row
        if (colIndex > splitterIndex && colIndex < showFrom) {
          // Hidden left columns
          if (colIndex === splitterIndex + 1) {
            // Draw first span column
            const from = splitterIndex + 1
            const to = showFrom - 1
            let width = 0
            for (let i = from; i <= to; i++) width += colWidths[i]
            return { name: 'left', width, from, to }
          } else {
            // Skip other columns
            return {}
          }
        } else if (colIndex > showTo) {
          // Hidden right columns
          if (colIndex === showTo + 1) {
            // Draw first span column
            const from = showTo + 1
            const to = colCount - 1
            let width = 0
            for (let i = from; i <= to; i++) width += colWidths[i]
            return { name: 'right', width, from, to }
          } else {
            // Skip other columns
            return {}
          }
        } else {
          // Common columns
          if (colIndex < splitterIndex) {
            // Fixed must have left property
            return {
              name: 'cell',
              left: colLefts[colIndex],
              width: colWidths[colIndex]
            }
          } else if (colIndex === splitterIndex) {
            // Splitter must have left property
            return {
              name: 'splitter',
              left: colLefts[colIndex],
              width: colWidths[colIndex]
            }
          } else {
            if (mergeCount > 1 && mergeRows.includes(rowIndex)) {
              // Merging cells
              if (0 === (colIndex - showFrom) % mergeCount) {
                // Span first cell
                const from = colIndex
                const to = colIndex + mergeCount - 1 < colCount ? colIndex + mergeCount - 1 : colCount - 1
                let width = 0
                for (let i = from; i <= to; i++) width += colWidths[i]
                return {
                  name: 'merge',
                  from,
                  to,
                  width
                }
              } else {
                // Skip merged cells
                return {}
              }
            } else {
              // Showing no fixed cells must have only width property
              const name = colFrom > colIndex || colTo < colIndex ? 'empty' : 'cell'
              return {
                name,
                width: colWidths[colIndex]
              }
            }
          }
        }
      }
    },
    [
      clientWidth,
      colCount,
      colLefts,
      colWidths,
      showFrom,
      showTo,
      colFrom,
      colTo,
      spanColIndex,
      spanRowIndex,
      splitterIndex,
      mergeRows,
      mergeCount
    ]
  )

  const getZIndex = useCallback(
    (rowIndex, colIndex) =>
      colIndex === splitterIndex
        ? 200 + rowCount - rowIndex - colIndex
        : colIndex < fixedColumns
        ? 300 - rowCount - rowIndex - colIndex
        : undefined /*rowCount - rowIndex*/,
    [splitterIndex, rowCount, fixedColumns]
  )

  const getStyle = useCallback(
    (rowIndex, colIndex, type) => {
      const collapseOption = collapse.find(v => v.rows.includes(rowIndex))
      const collapseTop = collapseOption?.top
      const collapseLeft = (colIndex - showFrom) % mergeCount > 0 && collapseOption?.left
      const collapseColor = collapseOption?.color
      const layout = props.layout || []
      if (!type.name) {
        // Skip cell
        return null
      } else if (['span', 'left', 'right', 'merge'].includes(type.name)) {
        // Spanned cells style
        const style = {
          boxSizing: 'border-box',
          width: `${type.width}px`,
          gridColumnStart: `${type.from + 1}`,
          gridColumnEnd: `${type.to + 2}`,
          borderLeftWidth: `${0 === colIndex ? 0 : props.gapWidth}px`,
          borderTopWidth: `${0 === rowIndex || collapseTop ? 0 : props.gapHeight}px`
        }
        if (type.left >= 0) {
          // If left assigned then cell is fixed
          style.position = 'sticky'
          style.left = `${type.left}px`
        }
        return { ...style, ...layout[rowIndex] }
      } else {
        // Common cell style
        const style = {
          boxSizing: 'border-box',
          width: `${type.width}px`,
          zIndex: getZIndex(rowIndex, colIndex)
        }
        if (type.left >= 0) {
          // If left assigned then cell is fixed
          style.position = 'sticky'
          style.left = `${type.left}px`
        }
        if ('splitter' === type.name) {
          // Style for splitter
          style.borderLeftWidth = `${props.gapWidth}px`
          style.borderTopWidth = '0'
          if (0 === rowIndex) {
            // style.borderTopLeftRadius = `${props.splitterWidth}px`
            style.borderTopRightRadius = `${props.splitterWidth}px`
          } else if (rowCount - 1 === rowIndex) {
            // style.borderBottomLeftRadius = `${props.splitterWidth}px`
            style.borderBottomRightRadius = `${props.splitterWidth}px`
          }
        } else {
          // Common cell
          // First column has no left border
          style.borderLeftWidth = `${0 === colIndex /*|| collapseLeft*/ ? 0 : props.gapWidth}px`
          if (collapseLeft) style.borderColor = collapseColor
          // First row has no top border
          style.borderTopWidth = `${0 === rowIndex || collapseTop ? 0 : props.gapHeight}px`
        }
        return { ...style, ...layout[rowIndex] }
      }
    },
    [
      collapse,
      showFrom,
      mergeCount,
      props.layout,
      props.gapWidth,
      props.gapHeight,
      props.splitterWidth,
      getZIndex,
      rowCount
    ]
  )

  const getDataColIndex = useCallback(
    colIndex => (splitterIndex > 0 && colIndex > splitterIndex ? colIndex - 1 : colIndex),
    [splitterIndex]
  )

  const hoverRef = useRef()
  hoverRef.current = (action, colIndex) => () => {
    if (props.onHover && colIndex > 0 && colIndex !== splitterIndex) {
      const index = colIndex < splitterIndex ? colIndex - 1 : colIndex - 2
      props.onHover({ action, index })
    }
  }

  const getElement = useCallback(
    (rowIndex, colIndex, key) => {
      const type = getCellType(rowIndex, colIndex)
      const style = getStyle(rowIndex, colIndex, type)
      if (style) {
        if (['left', 'right', 'empty'].includes(type.name)) {
          // Left and right spanned elements are empty div's
          return <div key={key} className={`${className}-cell`} style={style} />
        } else if (['span'].includes(type.name)) {
          // Span cell is for component returned by render span function
          const span = renderSpan.current()
          return (
            <div key={'span'} className={`${className}-cell`} style={style}>
              {span}
            </div>
          )
        } else {
          if ('splitter' === type.name) {
            // Render splitter
            const contentClass =
              0 === rowIndex
                ? `${className}-splitter-content ${className}-splitter-content-top`
                : rowCount - 1 === rowIndex
                ? `${className}-splitter-content ${className}-splitter-content-bottom`
                : `${className}-splitter-content`
            return (
              <div key={key} className={`${className}-splitter`} style={style}>
                <div className={contentClass}></div>
              </div>
            )
          } else {
            // Render common data cell
            return (
              <div
                key={key}
                className={`${className}-cell`}
                style={style}
                data-col-index={colIndex}
                data-row-index={rowIndex}
                data-cell-type={type.name}
                data-cell-key={key}
                onPointerEnter={hoverRef.current('enter', colIndex)}
                onPointerLeave={hoverRef.current('leave', colIndex)}
                onPointerMove={hoverRef.current('move', colIndex)}
              >
                {rows[rowIndex][getDataColIndex(colIndex)]}
              </div>
            )
          }
        }
      } else {
        // Skip rendering for spanned cells
        return null
      }
    },
    [getCellType, getStyle, className, rowCount, rows, getDataColIndex]
  )

  return {
    rowCount,
    colCount,
    splitterIndex,
    templateColumns,
    showFrom,
    showTo,
    getStyle,
    getElement,
    getCellType
  }
}

export default useCells
